import React, { useState } from 'react';
import { AppContext } from '../../../AppContext';
import styled, { css } from 'styled-components';
import Dropdown from '../../../core/components/Dropdown/Dropdown';
import { NavLink, useHistory } from 'react-router-dom';
import { Button, Icon, Loader } from '../../../core/components';
import {
  InfoMessageService,
  StyleService,
  TranslationService,
  UserLanguageService,
} from '../../../core/services';
import LanguageService from '../../../core/services/LanguageService';
import { codeFromCulture } from '../../../core/i18n/languages';
import NavigationApi from './api/NavigationApi';
import styles from './Navigation.module.scss';
import classNames from 'classnames';
import withAbortRequest, { AbortRequestPropsType } from '../../../core/hoc/withAbortRequest';
import { PuzzelChat } from '../../../logic/components';
import { DictionaryType } from '../../../core/types/DictionaryType';
import { useQuery } from 'react-query';
import { ActionStatusType } from '../../../core/types/ActionStatusType';

interface TopMenuSettings {
  customFlagIcon: string;
  customLabelTranslationKey: string;
  areListFlagVisible: boolean;
}

interface MenuElementsType extends TopMenuSettings {
  menuElements: MenuElementType[];
}

type MenuElementType = {
  items: any;
  menuId: string;
  name: string;
  url: string;
  icon: string;
  ariaLabel: string;
};

const Navigation = (props: {} & AbortRequestPropsType) => {
  const context = React.useContext(AppContext);
  const history = useHistory();
  const userLanguage = UserLanguageService.getLanguageCode();
  const [profileMenuItem, setProfileMenuItem] = useState<MenuElementType>();
  const [languageMenuItem, setLanguageMenuItem] = useState<MenuElementType>();
  const [logoutMenuItem, setLogoutMenuItem] = useState<MenuElementType>();
  const [menuElements, setMenuElements] = useState<MenuElementType[]>([]);
  const [isShowingChatInMenu, setIsShowingChatInMenu] = useState<boolean>(true);
  const [isShowingChatModal, setIsShowingChatModal] = useState<boolean>(false);
  const [isOpenDropdownMenu, setIsOpenDropdownMenu] = useState<boolean>(false);
  const [isOpenDropdown, setIsOpenDropdown] = useState<boolean>(false);

  const whiteLabelingStyles = StyleService.apply('module:header:navbar', context.styles);

  const menuSettings = useQuery<ActionStatusType<MenuElementsType>>(
    'menuElements',
    NavigationApi.getMenuElements,
    {
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response.success) {
          if (response.data.hasOwnProperty('menuElements')) {
            const menuElementComputed = response.data.menuElements
              .filter((element) => {
                switch (element.menuId) {
                  case 'LogoutMenuLink':
                    setLogoutMenuItem(element);
                    return false;
                  case 'LanguagesMenuLink':
                    setLanguageMenuItem(element);
                    return false;
                  case 'ProfileMenuLink':
                    setProfileMenuItem(element);
                    return false;
                  case 'ChatMenu':
                    return !context.isHideMypageCases;
                  default:
                    return true;
                }
              })
              .map((element) => {
                let icon = '';
                let url = element.url;
                let ariaLabel = '';

                switch (element.menuId) {
                  case 'HomeMenuLink':
                    icon = 'fas fa-home';
                    url = '/Home';
                    ariaLabel = 'Home';
                    break;
                  case 'MessageMenuLink':
                    icon = 'fas fa-envelope';
                    url = '/Messages';
                    ariaLabel = 'Messages';
                    break;
                  case 'HelpMenuLink':
                    icon = 'fas fa-info-circle';
                    url = '/Help';
                    ariaLabel = 'Help';
                    break;
                  case 'ChatMenu':
                    icon = 'fas fa-comment-dots';
                    url = '';
                    ariaLabel = 'Chat';
                    break;
                }

                return {
                  ...element,
                  icon: icon,
                  url: url,
                  ariaLabel: ariaLabel,
                };
              });

            setMenuElements(menuElementComputed);
          }
        }
      },
    }
  );

  const handleLanguageClick = async (code: string) => {
    setIsOpenDropdown(!isOpenDropdown);
    try {
      const changeLanguageResult = await NavigationApi.changeLanguage(
        code,
        props.cancelTokenSource.token
      );

      if (changeLanguageResult.success) {
        const shortCode = codeFromCulture(code);
        if (shortCode) {
          LanguageService.set(shortCode);
          UserLanguageService.setLanguageCode(code);
        }
        window.location.reload();
      } else {
        throw changeLanguageResult.message;
      }
    } catch (err) {
      InfoMessageService.error(TranslationService.translate('GeneralErrorMsg'));
    }
  };

  const handleToggleDropdown = () => {
    setIsOpenDropdown(!isOpenDropdown);
    setIsOpenDropdownMenu(false);
  };

  const handleMenuClick = () => {
    setIsOpenDropdownMenu(!isOpenDropdownMenu);
    setIsOpenDropdown(false);
  };

  const handleChatClick = async () => {
    setIsShowingChatModal(true);
  };

  const handleCloseModal = () => {
    setIsShowingChatModal(false);
  };

  const handleCloseChat = () => {
    setIsShowingChatModal(false);
  };

  const handleDisableChat = () => {
    setIsShowingChatInMenu(false);
  };

  return (
    <>
      <Nav className={styles.wrapper} styles={whiteLabelingStyles}>
        {menuSettings.isLoading ? (
          <Loader />
        ) : (
          <>
            <div className={classNames('show-lg', styles.group)}>
              {menuElements.map((element) => {
                if (element.menuId === 'ChatMenu' && isShowingChatInMenu) {
                  return (
                    <NavLink
                      exact
                      key={element.menuId}
                      to={element.url}
                      onClick={handleChatClick}
                      className={styles.link}
                      activeClassName={styles.linkActive}
                      id={element.name}
                      aria-label={element.ariaLabel}
                    >
                      <Icon
                        iconVariant={Icon.Variants.FontAwesome}
                        className={styles.linkIcon}
                        icon={element.icon}
                      />
                    </NavLink>
                  );
                } else {
                  return (
                    <NavLink
                      exact
                      key={element.menuId}
                      to={element.url}
                      className={styles.link}
                      activeClassName={styles.linkActive}
                      id={element.name}
                      aria-label={element.ariaLabel}
                      data-cy={`NavLink-${element.name}`}
                    >
                      <Icon
                        iconVariant={Icon.Variants.FontAwesome}
                        className={styles.linkIcon}
                        icon={element.icon}
                      />
                    </NavLink>
                  );
                }
              })}
            </div>

            <div className={classNames(styles.group, styles.groupToRight)}>
              {profileMenuItem && (
                <div className={classNames(styles.group, styles.groupToRight, 'show-lg')}>
                  <NavLink to="/UserProfile" id={'userProfile'} className={styles.profileLink}>
                    <Icon
                      iconVariant={Icon.Variants.FontAwesome}
                      className={styles.linkIcon}
                      icon="fas fa-user"
                    />
                    <p className={styles.profileLinkName}>
                      {TranslationService.translate('MyProfile')}
                    </p>
                  </NavLink>
                </div>
              )}

              {languageMenuItem && menuSettings.data && (
                <div className={classNames(styles.group, styles.dropdownLanguageWrapper)}>
                  <Dropdown
                    id="LanguageDropDown"
                    isOpen={isOpenDropdown}
                    toggle={handleToggleDropdown}
                    isAppendToBody={false}
                    className={styles.dropdown}
                  >
                    <Dropdown.Header classNames={styles.dropdownHeader}>
                      {menuSettings.data.data.customFlagIcon ? (
                        <i
                          className={classNames(
                            menuSettings.data.data.customFlagIcon,
                            styles.langIcon
                          )}
                        />
                      ) : (
                        <i
                          className={classNames(
                            userLanguage && styles[userLanguage],
                            styles.langIcon
                          )}
                        />
                      )}
                      {menuSettings.data.data.customLabelTranslationKey !== '' &&
                        TranslationService.translate(
                          menuSettings.data.data.customLabelTranslationKey
                        )}
                    </Dropdown.Header>

                    <Dropdown.List classNames={styles.langOptionList}>
                      {languageMenuItem.items?.map((lang: MenuElementType) => (
                        <Dropdown.ListItem
                          key={lang.name}
                          id={`LanguageOption-${lang.menuId}`}
                          className={styles.langOptionListItem}
                          onClick={() => handleLanguageClick(lang.menuId)}
                        >
                          {menuSettings.data?.data.areListFlagVisible && (
                            <i className={classNames(styles[lang.menuId], styles.langIcon)} />
                          )}
                          {TranslationService.translate(lang.name)}
                        </Dropdown.ListItem>
                      ))}
                    </Dropdown.List>
                  </Dropdown>
                </div>
              )}

              {logoutMenuItem && (
                <div className={classNames('show-lg', styles.group)}>
                  <NavLink
                    id={'logout'}
                    to={'/logout'}
                    className={styles.logoutLink}
                    onClick={() => {
                      history.push('/logout');
                    }}
                    aria-label="Logout"
                  >
                    <Icon
                      iconVariant={Icon.Variants.FontAwesome}
                      className={styles.linkIcon}
                      icon="fas fa-power-off"
                      dataCy="LogoutButton"
                    />
                  </NavLink>
                </div>
              )}

              <div className={classNames('hide-lg', styles.group)}>
                <Button
                  id={'burgerMenu'}
                  title={TranslationService.translate('BurgerMenu')}
                  variant={Button.Variants.Icon}
                  onClick={handleMenuClick}
                  classNames={styles.burgerButton}
                >
                  <Icon
                    iconVariant={Icon.Variants.FontAwesome}
                    className={styles.linkIcon}
                    icon="fas fa-bars"
                  />
                </Button>
              </div>

              <div
                className={classNames(
                  'hide-lg',
                  styles.dropDownMenu,
                  isOpenDropdownMenu && styles.isOpenDropdownMenu
                )}
              >
                {menuElements.length > 0 && (
                  <nav>
                    <>
                      {menuElements.map((element) => (
                        <a key={element.menuId} href={element.url} id={element.menuId}>
                          {element.name}
                          <Icon
                            iconVariant={Icon.Variants.FontAwesome}
                            className={styles.linkIcon}
                            icon={element.icon}
                          />
                        </a>
                      ))} 

                      {profileMenuItem && (
                        <a href="/UserProfile" className={styles.profileLink} id={'UserProfile'}>
                          {TranslationService.translate('Profile')}
                          <Icon
                            iconVariant={Icon.Variants.FontAwesome}
                            className={styles.linkIcon}
                            icon="fas fa-user"
                          />
                        </a>
                      )}

                      {logoutMenuItem && (
                        <NavLink
                          id={'logout'}
                          to={'/logout'}
                          className={styles.logoutLink}
                          onClick={() => {
                            history.push('/logout');
                          }}
                          aria-label="Logout"
                        >
                          {TranslationService.translate('Logout')}
                          <Icon
                            iconVariant={Icon.Variants.FontAwesome}
                            className={styles.linkIcon}
                            icon="fas fa-power-off"
                            dataCy="LogoutButtonBurgerMenu"
                          />
                        </NavLink>
                      )}
                    </>
                  </nav>
                )}
              </div>
            </div>
          </>
        )}
      </Nav>

      <PuzzelChat
        isShowing={isShowingChatModal}
        onClose={handleCloseModal}
        onCloseChat={handleCloseChat}
        disableChat={handleDisableChat}
      />
    </>
  );
};

const Nav = styled.nav<{ styles: DictionaryType<string> }>`
  ${(props) => css`
    --header-nav-color: ${props.styles.linkColor};
    --header-nav-color-hover: ${props.styles.linkHoverColor};
    --header-nav-background-color-hover: ${props.styles.linkHoverBackgroundColor};
    --header-nav-color-active: ${props.styles.linkActiveColor};
    --header-nav-background-color-active: ${props.styles.linkActiveBackgroundColor};
  `}
`;

export default React.memo(withAbortRequest(Navigation));
